import React, { useState, useRef, useEffect } from 'react'
import { withUserStore } from '../../contexts/UserStore'
import { Badge } from '@byscott-io/corebyscott'

const TagSelector = ({ taggable, userStore }) => {
  const [showDropdown, setShowDropdown] = useState(false)
  const [searchTerm, setSearchTerm] = useState('')
  const [tags, setTags] = useState([])
  const dropdownRef = useRef(null)

  // Get tags from the taggable object
  useEffect(() => {
    if (taggable && taggable.tags) {
      setTags(taggable.tags)
    }
  }, [taggable])

  // Get available tags from account
  const availableTags = userStore.account?.tags || []

  // Filter tags based on search term
  const filteredTags = availableTags.filter(tag =>
    tag.name.toLowerCase().includes(searchTerm.toLowerCase())
  )

  // Check if a tag is already applied
  const isTagApplied = (tag) => {
    return tags.some(t => t.id === tag.id)
  }

  // Add or create a tag
  const addTag = (tag) => {
    if (isTagApplied(tag)) return

    userStore.createTagging(
      taggable.recordType,
      taggable.id,
      tag.id,
      userStore.account.id,
      () => {
        setTags([...tags, tag])
        setSearchTerm('')
      }
    )
  }

  // Create a new tag
  const createNewTag = () => {
    if (!searchTerm.trim()) return

    userStore.createTag(searchTerm.trim(), userStore.account.id, (data) => {
      // Add the newly created tag to the taggable
      const createdTag = data.tag
      addTag(createdTag)
      setSearchTerm('')
    })
  }

  // Remove a tag
  const removeTag = (tag) => {
    // Find the tagging to delete by checking the taggable's taggings
    // For now, we'll need to use a simpler approach - just remove from UI
    // and let the backend handle cleanup, or we need to store tagging IDs
    // Let's assume we can query for it
    const taggingId = taggable.taggings?.find(t =>
      t.tagId === tag.id &&
      t.taggableType === taggable.recordType &&
      t.taggableId === taggable.id
    )?.id

    if (taggingId) {
      userStore.destroyTagging(taggingId, () => {
        setTags(tags.filter(t => t.id !== tag.id))
      })
    } else {
      // Fallback: just remove from UI
      setTags(tags.filter(t => t.id !== tag.id))
    }
  }

  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false)
        setSearchTerm('')
      }
    }

    if (showDropdown) {
      document.addEventListener('mousedown', handleClickOutside)
      return () => document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [showDropdown])

  const exactMatch = filteredTags.find(tag =>
    tag.name.toLowerCase() === searchTerm.toLowerCase()
  )

  return (
    <div className="cbs-scope" style={{ position: 'relative', display: 'inline-block' }} ref={dropdownRef}>
      {/* Display applied tags */}
      {tags.map(tag => (
        <Badge
          key={tag.id}
          color="primary"
          className="me-1"
          style={{ cursor: 'pointer' }}
          onClick={(e) => {
            e.stopPropagation()
            removeTag(tag)
          }}
        >
          {tag.name} ×
        </Badge>
      ))}

      {/* Dropdown trigger button */}
      {showDropdown ? (
        <input
          type="text"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault()
              if (exactMatch) {
                addTag(exactMatch)
              } else if (searchTerm.trim()) {
                createNewTag()
              }
            } else if (e.key === 'Escape') {
              setShowDropdown(false)
              setSearchTerm('')
            }
          }}
          placeholder="Search or create tag..."
          autoFocus
          style={{
            border: '1px solid #ccc',
            borderRadius: '4px',
            padding: '4px 8px',
            fontSize: '0.875rem',
            width: '200px'
          }}
          onClick={(e) => e.stopPropagation()}
        />
      ) : (
        <span
          style={{ cursor: 'pointer', display: 'inline-block' }}
          onClick={(e) => {
            e.stopPropagation()
            setShowDropdown(true)
          }}
          title="Add tags"
        >
          <i className="fas fa-tags" style={{ color: '#0d6efd' }} />
        </span>
      )}

      {/* Dropdown menu */}
      {showDropdown && (
        <div
          style={{
            position: 'absolute',
            top: '100%',
            left: 0,
            backgroundColor: 'white',
            border: '1px solid #ccc',
            borderRadius: '4px',
            marginTop: '4px',
            minWidth: '200px',
            maxHeight: '200px',
            overflowY: 'auto',
            zIndex: 1000,
            boxShadow: '0 2px 8px rgba(0,0,0,0.15)'
          }}
        >
          {filteredTags.length === 0 && searchTerm.trim() && (
            <div
              style={{
                padding: '8px 12px',
                cursor: 'pointer',
                fontSize: '0.875rem'
              }}
              onClick={(e) => {
                e.stopPropagation()
                createNewTag()
              }}
            >
              Create &ldquo;{searchTerm}&rdquo;
            </div>
          )}
          {filteredTags.map(tag => (
            <div
              key={tag.id}
              style={{
                padding: '8px 12px',
                cursor: 'pointer',
                fontSize: '0.875rem',
                backgroundColor: isTagApplied(tag) ? '#f0f0f0' : 'white'
              }}
              onClick={(e) => {
                e.stopPropagation()
                if (!isTagApplied(tag)) {
                  addTag(tag)
                }
              }}
              onMouseEnter={(e) => {
                if (!isTagApplied(tag)) {
                  e.target.style.backgroundColor = '#f8f9fa'
                }
              }}
              onMouseLeave={(e) => {
                if (!isTagApplied(tag)) {
                  e.target.style.backgroundColor = 'white'
                }
              }}
            >
              {tag.name} {isTagApplied(tag) && '✓'}
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

export default withUserStore(TagSelector)
